import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { Editor } from "@craftjs/core";
import { AppContainer, cn } from "twyne-new.ui";

import { useBuilderStore, BuilderStoreProvider } from "@/components/builder/utils/store";
import { useDefaultTailwindConfig } from "@/components/builder/utils/tailwindConfig/hooks";
import BuilderContent from "@/modules/cms/views/post/components/builder/Content/Builder";
import Header from "@/modules/cms/views/post/components/builder/Content/Header";
import LeftSidebar from "@/modules/cms/views/post/components/builder/Content/LeftSidebar";
import RightSidebar from "@/modules/cms/views/post/components/builder/Content/RightSidebar";
import Sidebar from "@/modules/cms/views/post/components/builder/Content/Sidebar";
import { BuilderProvider, useCraft, useEditing, useInteractionMode, useAutoSave } from "@/modules/cms/views/post/components/builder/context";
import useAutoSelectEditing from "@/modules/cms/views/post/components/builder/hooks/useAutoSelectEditing";
import useCompiler from "@/modules/cms/views/post/components/builder/hooks/useCompiler";
import { HistoryProvider } from "@/modules/cms/views/post/components/builder/hooks/useHistory";

import { PageBuilderStoreProvider, usePageBuilderStore } from "../builder/store";

function Content() {
  const { postBuilder: { history: { preview } } } = useBuilderStore()
  useAutoSelectEditing()
  useDefaultTailwindConfig()

  return <>
    <AppContainer direction='vertical' className={cn(preview && "cursor-not-allowed")}>
      <Header page={null} title={null} />
      <AppContainer className={cn("relative", preview && "pointer-events-none")} variant='none' id='builder-content'>
        <LeftSidebar defaultValue='assets' pages={null} />
        <AppContainer direction='vertical' variant='none'>
          <BuilderContent />
        </AppContainer>
        <Sidebar size='96' open={true}>
          <RightSidebar />
        </Sidebar>
      </AppContainer>
    </AppContainer>
  </>
}

function Builder() {
  const { value: editing } = useEditing()
  const { value: interactionMode } = useInteractionMode()
  const { resolver } = useCompiler()
  const { setValue: setCraft, value: craft } = useCraft()
  const { handleAutoSave } = useAutoSave()

  const { postBuilder: { history: { preview } } } = useBuilderStore()

  useEffect(() => {
    if (craft && editing && !preview) {
      editing.definition.craft = craft
      handleAutoSave({ craft })
    }
  }, [editing, craft, preview, handleAutoSave])

  const handleChange = useCallback(query => {
    const craft = query.getSerializedNodes()
    setCraft(craft)
  }, [setCraft])

  return <Editor resolver={resolver} onNodesChange={handleChange} enabled={interactionMode !== 'select' && !preview}>
    <HistoryProvider>
      <Content />
    </HistoryProvider>
  </Editor>
}

function BuilderContext() {
  const { postBuilder: { useLoad: useLoadBuilder, history: { preview } } } = useBuilderStore()
  const { id } = useParams()
  const key = useMemo(() => preview?.id || 'default', [preview])

  useLoadBuilder(id)

  return <BuilderProvider key={key}>
    <Builder />
  </BuilderProvider>
}

function BuilderStoreAdapter({ children }) {
  const store = usePageBuilderStore()
  return <BuilderStoreProvider store={store}>
    {children}
  </BuilderStoreProvider>
}

export default function BuilderForm() {
  return <PageBuilderStoreProvider>
    <BuilderStoreAdapter>
      <BuilderContext />
    </BuilderStoreAdapter>
  </PageBuilderStoreProvider>
}
